/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Tab, TabList, TabPanels, Tabs } from '@carbon/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';

import { history } from 'utils/history';
import IdpExtractionInProgressWarningModal from 'App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/idp-extraction-in-progress-warning-modal/IdpExtractionInProgressWarningModal';
import { idpProjectStore, idpApplicationStore } from 'stores';
import { useNavigationGuard } from 'App/Pages/Project/IdpApplication/idp-project/hooks/useNavigationGuard';
import { ClusterNotification } from 'App/Pages/Project/IdpApplication/idp-project/cluster-notification';
import { useClusterConnectorSecrets } from 'App/Pages/Project/IdpApplication/hooks';

import * as Styled from './ExtractionTabContainer.styled';

/** @typedef {{uploadDocuments: number, extractData: number, validateExtraction: number}} TabIndices */
/** @typedef {{slug: string, tab: string}} RouteParams */

/**
 * @param {{
 *  tabs: string[],
 *  tabIndices: TabIndices,
 *  selectedTabIndex: number,
 *  setSelectedTabIndex: (index: number) => void,
 *  tabTitles: string[],
 *  tabContents: JSX.Element[],
 *  disabledTabIndices?: number[],
 *  canUserModifyProject: boolean
 * }} props
 * @returns {JSX.Element}
 */
function ExtractionTabContainerComponent({
  tabs,
  tabIndices,
  selectedTabIndex,
  setSelectedTabIndex,
  tabTitles,
  tabContents,
  disabledTabIndices = [],
  canUserModifyProject
}) {
  const [targetTabIndex, setTargetTabIndex] = useState(null);
  /** @type {RouteParams} */
  const { slug, tab } = useParams();
  const { idpApplication } = idpApplicationStore;
  const areIdpConnectorSecretsMissing = useClusterConnectorSecrets(idpApplication?.clusterId);

  const {
    isModalOpen: isNavigationGuardModalOpen,
    handleConfirm: handleConfirmNavigation,
    handleCancel: handleCancelNavigation
  } = useNavigationGuard({
    isDirty: idpProjectStore.isExtractionInProgress
  });

  useEffect(() => {
    const isNoTabSelected = () => !tab || tabs.indexOf(tab) === -1;

    if (isNoTabSelected()) {
      history.push(`/idp-projects/${slug}/${tabs[tabIndices.uploadDocuments]}`);
      setSelectedTabIndex(tabIndices.uploadDocuments);
    }
  }, [slug, tab, tabs, tabIndices.uploadDocuments, setSelectedTabIndex]);

  const handleTabChange = ({ selectedIndex }) => {
    if (disabledTabIndices.includes(selectedIndex)) {
      return;
    }

    if (idpProjectStore.isExtractionInProgress) {
      setTargetTabIndex(selectedIndex);
    } else {
      history.push(`/idp-projects/${slug}/${tabs[selectedIndex]}`);
      setSelectedTabIndex(selectedIndex);
    }
  };

  const handleConfirmTabChange = () => {
    idpProjectStore.isExtractionInProgress = false;
    if (targetTabIndex !== null) {
      history.push(`/idp-projects/${slug}/${tabs[targetTabIndex]}`);
      setSelectedTabIndex(targetTabIndex);
      setTargetTabIndex(null);
    }
  };

  const renderClusterNotification = () => {
    if (!canUserModifyProject || !idpApplication?.clusterId) {
      return null;
    }
    return (
      <ClusterNotification
        clusterId={idpApplication.clusterId}
        areIdpConnectorSecretsMissing={areIdpConnectorSecretsMissing}
      />
    );
  };

  return (
    <>
      <Styled.TabsContainer>
        <Tabs selectedIndex={selectedTabIndex} onChange={handleTabChange}>
          <TabList aria-label="IDP project tabs">
            {tabTitles.map((title, i) => (
              <Tab key={`tab-${tabs[i]}`} role="tab">
                {title}
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {tabContents.map((content, i) => (
              <Styled.TabPanel key={`tab-panel-${tabs[i]}`}>
                {renderClusterNotification()}
                {selectedTabIndex === i && content}
              </Styled.TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Styled.TabsContainer>
      <IdpExtractionInProgressWarningModal
        isOpen={isNavigationGuardModalOpen || targetTabIndex !== null}
        modalText="Extraction is in progress. Navigating away will result in a loss of extraction results."
        onConfirm={targetTabIndex !== null ? handleConfirmTabChange : handleConfirmNavigation}
        onCancel={targetTabIndex !== null ? () => setTargetTabIndex(null) : handleCancelNavigation}
      />
    </>
  );
}

export const ExtractionTabContainer = observer(ExtractionTabContainerComponent);
